import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { queryClient } from "@/config/react-query";
import type { EnrollmentFetchParams, OutreachSequenceCreate, OutreachSequenceFetchParams, OutreachSequenceUpdate } from "@/types/outreach-sequence";
import { activateOutreachSequence, createOutreachSequence, deleteOutreachSequence, fetchEnrollmentExecutions, fetchEnrollments, fetchOutreachSequences, getOutreachSequence, pauseOutreachSequence, updateOutreachSequence } from "@/api/provider/comms/outreach-sequence";

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const outreachSequenceQueryKeys = {
  all: ["outreach-sequences"] as const,
  list: (params?: OutreachSequenceFetchParams) => [...outreachSequenceQueryKeys.all, "list", params] as const,
  detail: (id: string) => [...outreachSequenceQueryKeys.all, "detail", id] as const,
  enrollments: (id: string, params?: EnrollmentFetchParams) => [...outreachSequenceQueryKeys.all, "enrollments", id, params] as const,
  executions: (sequenceId: string, enrollmentId: string) => [...outreachSequenceQueryKeys.all, "executions", sequenceId, enrollmentId] as const
};

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useOutreachSequences(params) {
  const $ = _c(7);
  let t0;
  if ($[0] !== params) {
    t0 = outreachSequenceQueryKeys.list(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = () => fetchOutreachSequences(params);
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
}
export function useOutreachSequence(sequenceId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== sequenceId) {
    t0 = outreachSequenceQueryKeys.detail(sequenceId);
    $[0] = sequenceId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== sequenceId) {
    t1 = () => getOutreachSequence(sequenceId);
    $[2] = sequenceId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!sequenceId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useEnrollments(sequenceId, params) {
  const $ = _c(10);
  let t0;
  if ($[0] !== params || $[1] !== sequenceId) {
    t0 = outreachSequenceQueryKeys.enrollments(sequenceId, params);
    $[0] = params;
    $[1] = sequenceId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== params || $[4] !== sequenceId) {
    t1 = () => fetchEnrollments(sequenceId, params);
    $[3] = params;
    $[4] = sequenceId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!sequenceId;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}
export function useEnrollmentExecutions(sequenceId, enrollmentId) {
  const $ = _c(10);
  let t0;
  if ($[0] !== enrollmentId || $[1] !== sequenceId) {
    t0 = outreachSequenceQueryKeys.executions(sequenceId, enrollmentId);
    $[0] = enrollmentId;
    $[1] = sequenceId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== enrollmentId || $[4] !== sequenceId) {
    t1 = () => fetchEnrollmentExecutions(sequenceId, enrollmentId);
    $[3] = enrollmentId;
    $[4] = sequenceId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!enrollmentId;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateOutreachSequence() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onSuccess: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp2(_data, variables) {
  queryClient.invalidateQueries({
    queryKey: outreachSequenceQueryKeys.all
  });
  posthog.capture("outreach_sequence_saved", {
    is_new: true,
    has_recurrence: Boolean(variables.rrule),
    step_count: variables.steps?.length ?? 0,
    is_marketing: variables.isMarketing ?? true,
    cooldown_days: variables.cooldownDays ?? 90
  });
}
function _temp(payload) {
  return createOutreachSequence(payload);
}
export function useUpdateOutreachSequence() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: _temp4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp4(_data, variables) {
  queryClient.invalidateQueries({
    queryKey: outreachSequenceQueryKeys.all
  });
  posthog.capture("outreach_sequence_saved", {
    is_new: false,
    has_recurrence: Boolean(variables.payload.rrule),
    step_count: variables.payload.steps?.length ?? 0,
    is_marketing: variables.payload.isMarketing ?? true,
    cooldown_days: variables.payload.cooldownDays ?? 90
  });
}
function _temp3(t0) {
  const {
    sequenceId,
    payload
  } = t0;
  return updateOutreachSequence(sequenceId, payload);
}
export function useDeleteOutreachSequence() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp5,
      onSuccess: _temp6
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp6() {
  queryClient.invalidateQueries({
    queryKey: outreachSequenceQueryKeys.all
  });
}
function _temp5(sequenceId) {
  return deleteOutreachSequence(sequenceId);
}
export function useActivateOutreachSequence() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp7,
      onSuccess: _temp8
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp8() {
  queryClient.invalidateQueries({
    queryKey: outreachSequenceQueryKeys.all
  });
}
function _temp7(t0) {
  const {
    sequenceId,
    sendImmediately
  } = t0;
  return activateOutreachSequence(sequenceId, sendImmediately);
}
export function usePauseOutreachSequence() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp9,
      onSuccess: _temp10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp10() {
  queryClient.invalidateQueries({
    queryKey: outreachSequenceQueryKeys.all
  });
}
function _temp9(sequenceId) {
  return pauseOutreachSequence(sequenceId);
}